/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import { getFirebaseBackend } from '../../../helpers/firebase_helper';
import {
  postFakeLogin,
  postJwtLogin,
  postSocialLogin,
} from '../../../helpers/fakebackend_helper';
import {
  loginSuccess,
  loginOidcSuccess,
  logoutUserSuccess,
  apiError,
  reset_login_flag,
  logoutOidcSuccess,
  showLoader,
  hideLoader,
} from './reducer';
import axios from 'axios';
import { api, web } from '../../../config';
import { userManager } from '../../../config/oidcConfig';
import { persistor } from '../../../store';
import { encryptAuthUser } from '../../../utils/crypto';
import {
  SESSION_ENDED_KEY,
  markActivity,
  announceSessionStarted,
} from '../../../helpers/sessionManager';
import { getDeviceId } from '../../../helpers/deviceId';
import { getLoginBlockedError } from '../../../pages/Authentication/loginErrorUtils';
axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

// Instance terpisah untuk endpoint login: interceptor global (helpers/api_helper &
// core/api_helper) mengubah error menjadi string/Error generik sehingga pesan dari BE
// (mis. "User is not registered in the application") hilang sebelum sampai ke sini.
const authHttp = axios.create({ baseURL: api.API_URL, withCredentials: true });

const getServerMessage = error =>
  error?.response?.data?.message || error?.message || '';

// Login melalui Page. Registered Supplier wajib MFA: BE membalas challenge (mfaRequired)
// tanpa token; challenge dikembalikan ke Login.js untuk langkah kode authenticator.
export const loginUser = (user, history) => async dispatch => {
  try {
    const response = await authHttp.post('/account/login', {
      username: user.username,
      password: user.password,
      deviceId: getDeviceId(),
    });
    const apiData = response?.data?.data;
    if (apiData?.mfaRequired) {
      return apiData;
    }
    await dispatch(finishLogin(apiData, history));
  } catch (error) {
    dispatch(
      apiError(
        getLoginBlockedError(error) || getServerMessage(error) || 'Login failed'
      )
    );
  }
};

// Langkah akhir login (password biasa atau setelah login-mfa): simpan user lalu masuk dashboard.
export const finishLogin = (apiData, history) => async dispatch => {
  try {
    if (!apiData?.token) {
      throw new Error('No user data found in API response');
    }
    const authUser = await fetchauthUser(apiData.token);

    // Reset jejak sesi lama supaya tidak langsung kena idle/force logout setelah login.
    localStorage.removeItem(SESSION_ENDED_KEY);
    markActivity(true);
    dispatch(loginSuccess(authUser));
    await announceSessionStarted();
    history('/dashboard');
  } catch (error) {
    dispatch(
      apiError(
        getLoginBlockedError(error) || getServerMessage(error) || 'Login failed'
      )
    );
  }
};

export const idamanLogin = () => async dispatch => {
  try {
    dispatch(showLoader()); // Mulai loader
    await new Promise(resolve => setTimeout(resolve, 500)); // Jeda 500ms
    await userManager.signinRedirect();
  } catch (error) {
    console.error('Idaman Login Error :', error);
    dispatch(hideLoader()); // Matikan loader jika redirect gagal
    dispatch(apiError(error.message || 'Redirect to OIDC failed'));
  }
};

export const completeOidcLogin = navigate => async dispatch => {
  try {
    const oidcUser = await userManager.signinRedirectCallback();
    if (!oidcUser || !oidcUser.id_token) {
      throw new Error('Gagal mendapatkan token dari Identity Provider.');
    }
    const response = await authHttp.post('/account/login-oidc', {
      token: oidcUser.id_token,
      deviceId: getDeviceId(),
    });
    const userData = response?.data?.data;
    if (!userData || !userData.token) {
      throw new Error(
        'Response dari server tidak valid. Data user atau token tidak ditemukan.'
      );
    }
    const authUser = await fetchauthUser(userData.token);

    localStorage.removeItem(SESSION_ENDED_KEY);
    markActivity(true);
    dispatch(loginOidcSuccess(authUser));
    await announceSessionStarted();
    navigate('/dashboard');
  } catch (error) {
    console.error('OIDC Callback Error:', error);
    let userFriendlyMessage = 'Login gagal. Silakan coba lagi.';
    if (error.response) {
      // Teruskan pesan BE apa adanya; LoginErrorAlert (formatLoginError) yang
      // menerjemahkannya menjadi pesan yang ramah untuk user.
      userFriendlyMessage =
        getLoginBlockedError(error) ||
        getServerMessage(error) ||
        'Terjadi kesalahan pada server.';
    } else if (error.message) {
      if (
        error.message.includes('Identity Provider') ||
        error.message.includes('server tidak valid')
      ) {
        userFriendlyMessage = error.message;
      }
    }
    dispatch(apiError(userFriendlyMessage));
    // Bawa pesan error ke halaman login lewat router state supaya
    // penyebab kegagalan terlihat (sebelumnya error "hilang" begitu
    // pindah halaman dan user hanya melihat balik ke /login tanpa sebab).
    navigate('/login', { state: { oidcError: userFriendlyMessage } });
  }
};

// Logout biasa (non-OIDC)
export const logoutUser = () => async dispatch => {
  try {
    if (process.env.REACT_APP_DEFAULTAUTH === 'firebase') {
      const fireBaseBackend = getFirebaseBackend();
      await fireBaseBackend.logout();
    }
    dispatch(logoutUserSuccess());
  } catch (error) {
    dispatch(apiError(error.message || 'Logout failed'));
  }
};

const handleLogout = async () => {
  // Tunggu clear selesai sebelum redirect
  await new Promise(resolve => {
    sessionStorage.clear();
    resolve();
  });
  window.location.href = '/login';
};

export const oidcLogout = () => async dispatch => {
  try {
    dispatch(logoutOidcSuccess()); // reset redux login state
    await persistor.purge(); // bersihkan redux-persist storage
    await userManager.signoutRedirect(); // redirect ke OIDC logout
  } catch (error) {
    dispatch(apiError(error.message || 'OIDC Logout failed'));
    // Re-throw: pemanggil (Logout.js) perlu tahu signoutRedirect gagal
    // untuk menjalankan fallback clear + redirect /login.
    throw error;
  }
};

// Callback setelah logout dari OIDC (mendarat di /signout-callback-oidc).
// urutan penting: proses signout callback dulu, BERSIHKAN sesi lokal,
// lalu navigate('/login'). Tanpa signoutRedirect() kedua — panggilan itu
// mengirim user balik ke IdP (loop) sehingga clearStaleState/removeUser/
// navigate tidak pernah tercapai.
export const oidcLogoutCallback = navigate => async dispatch => {
  try {
    await userManager.signoutRedirectCallback();
    await userManager.clearStaleState(); // membersihkan data yang kadaluarsa
    await userManager.removeUser(); // menghapus user dari storage oidc-client-ts
    dispatch(logoutOidcSuccess()); // reset redux login state
    await persistor.purge(); // bersihkan redux-persist storage
    navigate('/login'); // redirect ke halaman login
  } catch (error) {
    console.error('OIDC Logout Callback Error:', error);
    dispatch(apiError(error.message || 'Logout callback failed'));
    navigate('/error');
  }
};

// Login via Social (Firebase)
export const socialLogin = (type, history) => async dispatch => {
  try {
    if (process.env.REACT_APP_DEFAULTAUTH === 'firebase') {
      const fireBaseBackend = getFirebaseBackend();
      const response = await fireBaseBackend.socialLoginUser(type);
      if (response) {
        dispatch(loginSuccess(response, { meta: { isOidcUser: false } }));
        history('/dashboard');
      } else {
        throw new Error('Social login gagal');
      }
    }
  } catch (error) {
    dispatch(apiError(error.message || 'Social login failed'));
  }
};

export const resetLoginFlag = () => dispatch => {
  try {
    return dispatch(reset_login_flag());
  } catch (error) {
    dispatch(apiError(error.message || 'Reset login state failed'));
  }
};

const fetchauthUser = async token => {
  try {
    const response = await axios.get(`${api.API_URL}/account/my-profile`, {
      withXSRFToken: true,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: `application/json`,
      },
    });
    let userData;
    userData = response;
    console.log(userData);
    const authUser = {
      email: userData.email,
      name: userData.userName,
      username: userData.userName,
      token: token,
      status: 'success',
      firstname: userData.firstname,
      role: userData?.roles?.join(', ') ?? 'No Role',
      photo: userData.photo,
      division: userData?.roles?.join(', ') ?? 'No Role',
      position: userData?.jobTitle ?? userData?.position ?? '',
      displayName: userData?.displayName ?? '',
    };
    const encryptedAuthUser = encryptAuthUser(authUser);
    localStorage.setItem('authUser', JSON.stringify(encryptedAuthUser));

    return authUser;
  } catch (error) {
    console.error('Error fetching data from Invoice API:', error);
  }
};
